import React, { useEffect, useState } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { api } from '../../utils/api';
import ImageUploader from '../../components/ImageUploader';
import LoadingSpinner from '../../components/LoadingSpinner';
import {
  PageHeader, Panel, FormGroup, FormGrid, SaveBtn, SecondaryBtn, DangerBtn,
  Toast, Row, SectionCard,
} from './styles/AdminStyles';

const emptyMember = () => ({ name: '', role: '', bio: '', image: '' });

const EditTeam = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    api.get('/team').then(d => {
      setData({ ...d, members: Array.isArray(d.members) ? d.members : [] });
      setLoading(false);
    });
  }, []);

  if (loading || !data) return <LoadingSpinner fullPage />;

  const setField = (f) => (e) => setData({ ...data, [f]: e.target.value });
  const updateMember = (i, patch) => {
    const next = [...data.members];
    next[i] = { ...next[i], ...patch };
    setData({ ...data, members: next });
  };
  const addMember = () => setData({ ...data, members: [...data.members, emptyMember()] });
  const removeMember = (i) => setData({ ...data, members: data.members.filter((_, idx) => idx !== i) });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setToast(null);
    try {
      const saved = await api.put('/team', {
        title: data.title || '',
        description: data.description || '',
        bannerImage: data.bannerImage || '',
        members: data.members,
      });
      setData({ ...saved, members: Array.isArray(saved.members) ? saved.members : [] });
      api.clearCache();
      setToast({ type: 'success', message: 'Team page saved.' });
    } catch (err) {
      setToast({ type: 'error', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save}>
      <PageHeader>
        <div className="eyebrow">Content</div>
        <h1>Edit Team Page</h1>
        <p>Manage the team banner, intro and the list of team members.</p>
      </PageHeader>

      <Panel>
        <h2>Page Header</h2>
        <ImageUploader
          label="Banner Image"
          value={data.bannerImage}
          onChange={val => setData({ ...data, bannerImage: val })}
          onRemove={() => setData({ ...data, bannerImage: '' })}
        />
        <FormGroup>
          <label>Title</label>
          <input value={data.title || ''} onChange={setField('title')} />
        </FormGroup>
        <FormGroup>
          <label>Description</label>
          <textarea value={data.description || ''} onChange={setField('description')} />
        </FormGroup>
      </Panel>

      <Panel>
        <h2>Members</h2>
        <div className="subtitle">Displayed as cards on the public Team page.</div>

        {data.members.map((m, i) => (
          <SectionCard key={i}>
            <h3>
              Member {i + 1}{m.name ? ` — ${m.name}` : ''}
              <DangerBtn type="button" onClick={() => removeMember(i)}><FaTrash /> Remove</DangerBtn>
            </h3>
            <FormGrid $cols={2}>
              <FormGroup>
                <label>Name</label>
                <input value={m.name || ''} onChange={e => updateMember(i, { name: e.target.value })} />
              </FormGroup>
              <FormGroup>
                <label>Role</label>
                <input value={m.role || ''} onChange={e => updateMember(i, { role: e.target.value })} />
              </FormGroup>
            </FormGrid>
            <FormGroup>
              <label>Bio</label>
              <textarea value={m.bio || ''} onChange={e => updateMember(i, { bio: e.target.value })} />
            </FormGroup>
            <ImageUploader
              label="Photo"
              value={m.image}
              onChange={val => updateMember(i, { image: val })}
              onRemove={() => updateMember(i, { image: '' })}
            />
          </SectionCard>
        ))}

        <SecondaryBtn type="button" onClick={addMember}><FaPlus /> Add Member</SecondaryBtn>
      </Panel>

      <Row>
        <SaveBtn type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</SaveBtn>
        {toast && <Toast $error={toast.type === 'error'}>{toast.message}</Toast>}
      </Row>
    </form>
  );
};

export default EditTeam;
